/**
 * AP-58 tri-state for a fetched form definition (form-builder wave PR-4).
 *
 * The rule this encodes is the MoneySummary lesson (B-400) applied to the form
 * itself: a surface that cannot load its questions must SAY so. It must never
 * render an empty form — an /apply page with no questions and a Continue button
 * looks like a form the family completed, and a pre-screen with no questions
 * looks like a family that passed. Both are lies the family cannot detect.
 *
 * `components/trfer/SectionStates.tsx` encodes the same rule for staff blocks;
 * this is the family-facing twin, in the apply surface's own grammar.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCopy } from '@/hooks/useCopy';
import { useLocale } from '@/i18n/useLocale';
import type { FormDefinitionVersion } from '@/lib/formDefinitions';
import { localizeVersion, type LocalizedForm } from '@/lib/formI18n';

/** What the renderer needs to know about the language it is rendering in
 *  (#2109 R2-B1 / R2-B18): everything `localizeVersion` reports but the body. */
export type FormLocalization = Omit<LocalizedForm, 'version'>;

export type DefinitionState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      /** The version to RENDER: localised for the reader, `i18n` stripped. */
      version: FormDefinitionVersion;
      /** The version as served, English and translations both. Read it only
       *  where a rule must compare against the English (never to render). */
      source: FormDefinitionVersion;
      localization: FormLocalization;
    };

/** What the loader is asked for. `lang` is the reader’s UI locale; the server
 *  returns English plus only that code (R2-B19). */
export interface DefinitionLoadOptions { lang: string }

/** Which published form a gate loads. It picks the whole load-failure
 *  sentence, so no English noun is ever set into a translated frame.
 *  #2752: `renewal` is the application form on the /renew walk. */
export type FormGateSubject = 'application' | 'prescreen' | 'renewal';

/**
 * Fetch a published definition once, with an explicit retry, and localise it
 * for the reader (#2109 R2 lane L3).
 *
 * There is deliberately no cached/stale fallback: rendering last week's
 * questions because today's fetch failed is precisely the "never lies" rule
 * being broken quietly.
 *
 * Localisation happens HERE and only here: `localizeVersion` runs once per
 * (version, locale) inside a memo, and every renderer below reads the result
 * (R2-B1). A language switch refetches in the background and keeps the form
 * mounted on the version it already has — dropping back to the skeleton would
 * unmount the wizard and throw away the step the family is on.
 */
export function useFormDefinition(
  load: (options: DefinitionLoadOptions) => Promise<{ data?: FormDefinitionVersion }>,
  subject: FormGateSubject,
): DefinitionState & { retry: () => void } {
  const { locale } = useLocale();
  const { t, i18n } = useTranslation('forms');
  const [fetched, setFetched] = useState<
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'ready'; raw: FormDefinitionVersion; lang: string }
  >({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  /** #2752: the renewal line once the reader's language has it; until then
   *  that language's own application line rather than English. */
  const renewalLine = (): boolean => {
    if (subject !== 'renewal') return false;
    const lng = i18n.resolvedLanguage ?? i18n.language ?? 'en';
    if (lng === 'en' || lng.startsWith('en-')) return true;
    const value: unknown = i18n.getResource(lng, 'forms', 'gate.loadFailedRenewal');
    return typeof value === 'string' && value.trim() !== '';
  };
  const failed = subject === 'prescreen'
    ? t('gate.loadFailedPrescreen')
    : renewalLine() ? t('gate.loadFailedRenewal') : t('gate.loadFailedApplication');

  useEffect(() => {
    let cancelled = false;
    // A first load or a retry shows the skeleton; a language switch over a
    // form already on screen does not (see above).
    setFetched((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }));
    load({ lang: locale })
      .then((response) => {
        if (cancelled) return;
        const version = response?.data;
        // A 200 carrying no version is still a failure — treat a shape we
        // cannot render as an error rather than rendering nothing.
        if (!version || !Array.isArray(version.fields)) {
          setFetched({ status: 'error', message: failed });
          return;
        }
        setFetched({ status: 'ready', raw: version, lang: locale });
      })
      .catch(() => {
        if (cancelled) return;
        setFetched({ status: 'error', message: failed });
      });
    return () => { cancelled = true; };
    // `load` is an api accessor and stable in practice; attempt drives retries.
    // `failed` follows `subject` and the locale, both already listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, subject, locale]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // Localised with the language the version was FETCHED for, so a switch
  // lands all at once when the new response arrives rather than as English
  // for the moment in between.
  const localized = useMemo(
    () => (fetched.status === 'ready' ? localizeVersion(fetched.raw, fetched.lang) : null),
    [fetched],
  );

  if (fetched.status !== 'ready') return { ...fetched, retry };
  // Unreachable: `localized` is non-null whenever `fetched` is ready.
  if (!localized) return { status: 'loading', retry };
  const { version, ...localization } = localized;
  return { status: 'ready', version, source: fetched.raw, localization, retry };
}

/** Skeleton shaped like the question stack it replaces. */
export function DefinitionSkeleton() {
  const { t } = useTranslation('forms');
  return (
    <div className="space-y-6" aria-busy="true" aria-label={t('gate.loading')}>
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * The failure panel. It says what happened, offers a retry, and — crucially —
 * offers no way forward: a family must never be able to advance past questions
 * that failed to load.
 */
export function DefinitionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  // #1009 / AP-26: the reassurance line is a registered slot. `message` names
  // what failed to load and is interpolated by the caller — data, not a slot.
  // Guardrail 7: hold at the skeleton until the copy read has SETTLED, so the
  // seed never flickers into an override on the one screen a family is stuck on.
  // Latched to the first settle: mounting this observer refetches an errored
  // (always-stale) copy query, and an unlatched hold would blank the panel on
  // every later re-pend of that shared read.
  const { copy, ready } = useCopy();
  const { t } = useTranslation('common');
  const readyOnce = useRef(false);
  if (ready) readyOnce.current = true;
  if (!readyOnce.current) return <DefinitionSkeleton />;
  return (
    <div
      role="alert"
      className="rounded-lg border border-destructive/30 bg-destructive/5 px-5 py-6 text-center space-y-3"
    >
      <p className="text-body text-destructive font-medium">{message}</p>
      <p className="text-body-sm text-stevens-ink-3">
        {copy('apply.definition_gate.unavailable_body')}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('actions.retry')}
      </Button>
    </div>
  );
}

/** Render children only once the definition is genuinely available. */
export function DefinitionGate({
  state,
  children,
}: {
  state: DefinitionState & { retry: () => void };
  children: (
    version: FormDefinitionVersion,
    form: { source: FormDefinitionVersion; localization: FormLocalization },
  ) => React.ReactNode;
}) {
  if (state.status === 'loading') return <DefinitionSkeleton />;
  if (state.status === 'error') return <DefinitionError message={state.message} onRetry={state.retry} />;
  return <>{children(state.version, { source: state.source, localization: state.localization })}</>;
}
